import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import ConstituentLink from '@/components/common/ConstituentLink';
import ProfileDescriptorLink from '@/components/common/ProfileDescriptorLink';
import OpportunityClosedState from '@/constants/OpportunityClosedState';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { type DataListColumn } from '@/modules/DataList';
import { formatCurrencyWithDecimals } from '@/utils/currency';
import { formatDate } from '@/utils/dates';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import OpportunityMenu from '../components/OpportunityMenu';
import type OpportunitiesDataListRow from '../types/OpportunitiesDataListRow';

export default function useOpportunitiesDataListColumns({
  showActions = true,
  showConstituent = true,
  shouldRemoveOnArchive = true,
  shouldRemoveOnUnarchive = true,
}: {
  showActions?: boolean;
  showConstituent?: boolean;
  shouldRemoveOnArchive?: boolean;
  shouldRemoveOnUnarchive?: boolean;
} = {}) {
  const ownerOrgId = useOwnerOrgId();

  return React.useMemo(() => {
    const result: DataListColumn<OpportunitiesDataListRow>[] = [
      {
        columnKey: 'createdAt',
        headerName: 'DATE_LABEL',
        render: ({ created }: OpportunitiesDataListRow) => formatDate(created),
      },
      ...(showConstituent
        ? [
            {
              columnKey: 'constituentName',
              headerName: 'OPPORTUNITIES_CONSTITUENT_LABEL',
              render: ({ constituent }: OpportunitiesDataListRow) => {
                if (!constituent) {
                  return null;
                }

                return (
                  <ConstituentLink
                    constituentId={constituent.constituentId}
                    displayName={constituent.displayName}
                  />
                );
              },
            },
          ]
        : []),
      {
        columnKey: 'opportunityStageOrder',
        headerName: 'OPPORTUNITIES_STAGE_LABEL',
        render: 'stage',
      },
      {
        columnKey: 'confidenceLevel',
        headerName: 'OPPORTUNITIES_CONFIDENCE_LABEL',
        render: 'confidenceLevel',
      },
      {
        columnKey: 'owner',
        headerName: 'OPPORTUNITIES_OWNER_LABEL',
        render: ({ owner }: OpportunitiesDataListRow) => {
          if (!owner) {
            return null;
          }

          return (
            <ProfileDescriptorLink
              displayName={owner.displayName}
              ownerOrgId={ownerOrgId}
              userId={owner.userId}
            />
          );
        },
      },
      {
        columnKey: 'askAmount',
        headerName: 'OPPORTUNITIES_ASK_LABEL',
        TableHeaderCellProps: {
          className: 'text-right',
        },
        TableCellProps: {
          className: 'text-right',
        },
        render: ({ askAmount }: OpportunitiesDataListRow) => {
          return formatCurrencyWithDecimals(askAmount || 0, true);
        },
      },
      {
        columnKey: 'expectedAmount',
        headerName: 'OPPORTUNITIES_EXPECTED_LABEL',
        TableHeaderCellProps: {
          className: 'text-right',
        },
        TableCellProps: {
          className: 'text-right',
        },
        render: ({ expectedAmount }: OpportunitiesDataListRow) => {
          return formatCurrencyWithDecimals(expectedAmount || 0, true);
        },
      },
      {
        columnKey: 'projected',
        TableHeaderCellProps: {
          className: 'text-right',
        },
        TableCellProps: {
          className: 'text-right',
        },
        headerName: (
          <Tooltip title={translate('OPPORTUNITIES_PROJECTED_ACTUAL_TOOLTIP')}>
            <span>{translate('OPPORTUNITIES_PROJECTED_ACTUAL_LABEL')}</span>
          </Tooltip>
        ),
        render: ({
          actualValue,
          closedState,
          factoredAmount,
        }: OpportunitiesDataListRow) => {
          const factored = formatCurrencyWithDecimals(factoredAmount, true);
          return closedState ? (
            <span className="type-body2">
              {formatCurrencyWithDecimals(
                closedState === OpportunityClosedState.WON ? actualValue : 0,
                true,
              )}
            </span>
          ) : (
            factored
          );
        },
      },
    ];
    if (showActions) {
      result.push({
        columnKey: 'actions',
        headerName: '',
        align: 'right',
        render: (opportunity: OpportunitiesDataListRow) => {
          return <OpportunityMenu opportunity={opportunity} />;
        },
      });
    }
    return result;
  }, [shouldRemoveOnArchive, shouldRemoveOnUnarchive]);
}
